import type { AskViewBoardBlock } from './board-payload';
import type { AskViewPublishedOptions } from './loop2-payload';
import type { AskViewRecoveryOutcome } from './recovery-outcome-payload';
import type { AskViewRecommendationRecord } from './recommendation-payload';
import type { SynergyFigure } from '../../synergy-risk';
import type { SynergyVerificationView } from '../../synergy-verification';
import type { AskViewAddress, AskViewDecisionRow, AskViewThesisStatus } from './view-payload';
import type { AskViewPublishedAction } from './operating-review-payload';

/** Sparse observations and drawing coordinates authored by the API. Missing is never zero. */
export type AskViewCommitmentPoint = Readonly<{
  date: string; label: string; x: number;
  plan: SynergyFigure | null; realized: SynergyFigure | null;
  planBasis?: import('../../figure-period-basis').FigurePeriodBasis;
  realizedBasis?: import('../../figure-period-basis').FigurePeriodBasis;
  planY: number | null; realizedY: number | null;
}>;
export type AskViewCommitmentTrajectory = Readonly<{
  asOfPosition?: number; asOfLabel?: string;
  title: string; summary: string; missing: string | null;
  points: readonly AskViewCommitmentPoint[];
  ticks: readonly Readonly<{ label: string; y: number }>[];
  plan: SynergyFigure | null; realized: SynergyFigure | null; behind: SynergyFigure | null;
}>;
export type AskViewCommitmentDependency = Readonly<{
  title: string; name: string; owner: string;
  plannedDate: string; forecastDate: string; daysLate: SynergyFigure | null;
  committedFinish: string; forecastFinish: string;
  committedPosition: number | null; forecastPosition: number | null; asOfPosition: number;
  ticks: readonly Readonly<{ label: string; position: number }>[];
  address: AskViewAddress;
}>;
export type OrgModelLineage = 'same_lineage' | 'independent';
export const ORG_MODEL_VERDICTS = ['matches_source_model', 'differs_same_lineage',
  'confirms', 'shortfall', 'model_above_promise'] as const;
export type OrgModelVerdict = typeof ORG_MODEL_VERDICTS[number];
export const ORG_MODEL_VERDICT_TEXT: Readonly<Record<OrgModelVerdict, string>> = {
  matches_source_model: 'matches its source model (same lineage), not an independent check',
  differs_same_lineage: 'differs within the same lineage', confirms: 'confirms',
  shortfall: 'shortfall', model_above_promise: 'model above promise',
};
/** annualCost is null when any counted position has no published cost; counts still hold. */
export type OrgModelTotals = Readonly<{
  label: string; positionCount: SynergyFigure; annualCost: SynergyFigure | null;
  costedPositionCount: SynergyFigure; missingCostPositionCount: SynergyFigure;
}>;
export type OrgModelRoleEvidence = Readonly<{
  roleRef: string; title: string; sourceScenarioRef: string; sourceScenarioVersionRef: string;
  sourceReceiptRef: string; decisionRef: string; annualCost: SynergyFigure | null;
  positionCount: SynergyFigure; costedPositionCount: SynergyFigure;
  missingCostPositionCount: SynergyFigure;
}>;
export type OrgModelFunctionEvidence = OrgModelTotals & Readonly<{
  functionRef: string; roles: readonly OrgModelRoleEvidence[];
}>;
export type OrgModelAvailableEvidence = Readonly<{
  state: 'available'; asOf: string; modelRunAt: string;
  scenarioRef: string; scenarioVersionRef: string;
  sourceLine: string; drillSourceLine: string;
  promise: SynergyFigure;
  comparison: Readonly<{
    state: 'available'; lineage: OrgModelLineage; verdict: OrgModelVerdict;
    text: string; gapCents: string; gap: SynergyFigure;
  }> | Readonly<{
    state: 'unavailable'; lineage: null; verdict: null; text: string;
    gapCents: null; gap: null;
  }>;
  branch: OrgModelTotals; outsideBranch: OrgModelTotals; union: OrgModelTotals;
  drill: Readonly<{
    state: 'available' | 'unavailable'; message: string;
    functions: readonly OrgModelFunctionEvidence[];
  }>;
}>;
export type OrgModelUnavailableEvidence = Readonly<{
  state: 'unavailable'; message: string; asOf: string;
  promise: null; comparison: null; branch: null; outsideBranch: null; union: null;
}>;
export type OrgModelEvidence = OrgModelAvailableEvidence | OrgModelUnavailableEvidence;
/** Same-date pace of reported exits against planned exits; never the full promise. */
export const EXITS_PACE_VERDICTS = ['behind_plan', 'on_plan', 'ahead_of_plan'] as const;
export type ExitsPaceVerdict = typeof EXITS_PACE_VERDICTS[number];
export const EXITS_PRICING_BASES = ['role_cost', 'average_position_cost'] as const;
export type ExitsPricingBasis = typeof EXITS_PRICING_BASES[number];
/** The promise checked by actual exits: the count is independent of the promise's model. */
export type IndependentChallenge = Readonly<{
  state: 'available'; asOf: string; exitsThrough: string; title: string;
  verdict: ExitsPaceVerdict; valueVerdict: ExitsPaceVerdict; text: string;
  pricing: ExitsPricingBasis;
  exits: SynergyFigure; plannedExits: SynergyFigure; exitGap: SynergyFigure;
  realized: SynergyFigure; gap: SynergyFigure; promise: SynergyFigure;
  remaining: SynergyFigure | null; dueDate: string | null;
  countLine: string; pricingLine: string; remainingLine: string;
  independenceLine: string; sourceLine: string;
}> | Readonly<{
  state: 'unavailable'; asOf: string; title: string; message: string; sourceLine: string;
}>;
export type AskViewCommitmentBlock = Readonly<{
  measurement?: import('./operating-review-payload').AskViewRecoveryMeasurement;
  options?: AskViewPublishedOptions;
  orgModelEvidence?: OrgModelEvidence;
  independentChallenge?: IndependentChallenge;
  /** Additive engine-owned context for the Board lens. */
  board?: AskViewBoardBlock;
  outcome: AskViewRecoveryOutcome | null;
  /** Server-authored dated line when no recovery decision exists at the as-of; else null. */
  outcomeMissing: string | null;
  kind: 'commitment'; commitmentRef: string; label: string; owner: string;
  status: AskViewThesisStatus; pillar: Readonly<{ label: string; address: AskViewAddress }>;
  assumption: string;
  forecastFinish?: string | null;
  targetFinish?: string | null;
  yearEndLanding?: (Pick<import('./view-payload').AskViewHomeLanding, 'figure' | 'cases' | 'by'> &
    { promise?: SynergyFigure | null }) | null;
  recommendation?: AskViewRecommendationRecord;
  nextStep: Readonly<{ available?: boolean; next: string; why: string; statusText: string;
    address: AskViewAddress;
    /** The selected step's governed figures that its `why` cites, with their bindings. */
    figures?: readonly SynergyFigure[];
    /** The published move behind `next` and its bound value, as the Decide step carries it. */
    publishedAction?: AskViewPublishedAction | null }>;
  trajectory: AskViewCommitmentTrajectory;
  dependency: AskViewCommitmentDependency | null;
  dependencyMissing: string;
  weeklySignalsNote: string;
  /** The selected commitment's unchanged account and posting receipts at the page date. */
  ledgerReceipts?: Pick<SynergyVerificationView, 'asOf' | 'commitments'>;
  verification: Readonly<{ title: string; verified: SynergyFigure | null;
    reported?: SynergyFigure | null; confirmation?: 'yes' | 'partly' | 'none' | 'other';
    sourceLine?: string;
    unverified: SynergyFigure | null; verifiedShare: number | null;
    address: AskViewAddress }> | null;
  verificationMissing: string;
  decisions: Readonly<{ title: string; rows: readonly AskViewDecisionRow[];
    missing: string | null }>;
}>;
